import { default as React } from "react";
import { get, post } from "aws-amplify/api";
import { PacmanLoader } from "react-spinners";
import { Button, Col, Row, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";

import {
  createColumnHelper,
} from "@tanstack/react-table";

function EntityPropertySaleList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  userPermissions,
  saveRecord,
  validateRelatedRecord,
  deleteRecord,
  updateTableState,
  saleid,
  user,
  primaryCategory,
  idList,
  propertyidlist,
  areaSum,
  propertykey,
  propertyid,
  IsUnit,
  removeHold,
  refreshDataSet,
  getProperties,
  tableProfile,
}) {

  const formatSaleDate = (value) => {
    if (!value) {
      return "";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }

    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const dataSet = 'propertySales';
  const isEntitySalesProfile = tableProfile === "entity-sales";
  const resolvedControlType = isEntitySalesProfile && controlType === "table4"
    ? "entity-table4"
    : controlType;
  const resolvedPageSize = isEntitySalesProfile
    ? PAGE_SIZES.entityDetailSalesTab
    : PAGE_SIZES.holdChecksSalePropertiesSection;
  const trustProof = Boolean(userPermissions?.trust_proof);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [showErrorModal, setShowErrorModal] = React.useState(false);

  // --- TanStack Table Setup ---
  const tableData = Array.isArray(data) ? data : [];
  const columnHelper = createColumnHelper();
  const createId = React.useMemo(() => createElementIdFactory("entity-property-sale-list"), []);
  const tableElementIds = React.useMemo(
    () => createReusableTableElementIds(createId, controlType || "table"),
    [createId, controlType]
  );

  const col_sale_id = columnHelper.accessor("sale_id", {
    id: "sale_id",
    header: "ID",
    cell: (e) => (
      <Link
        to={{
          pathname: `/sales/${e.getValue()}`,
          state: {
            data: data.map((a) => a.sale_id),
            primaryCategory: primaryCategory,
            propertyidlist: idList,
          },
        }}
      >
        ${e.getValue()}
      </Link>
    ),
    size: 60,
  });
  const col_last_sale = columnHelper.accessor("last_sale", {
    id: "last_sale",
    header: "Last Sale",
    cell: EditableCell,
    size: 60,
    style: { textAlign: "center" },
  });
  const col_appendix = columnHelper.accessor("appendix", {
    id: "appendix",
    header: "Appendix",
    cell: EditableCell,
    size: 100,
    style: { textAlign: "right" },
  });
  const col_income = columnHelper.accessor("income", {
    id: "income",
    header: "Income",
    size: 200,
    style: { textAlign: "right" },
  });
  const col_income_basis = columnHelper.accessor("income_basis", {
    id: "income_basis",
    header: "Income Basis",
    size: 200,
  });
  const col_yield = columnHelper.accessor("yield", {
    id: "yield",
    header: "Yield",
    cell: EditableCell,
    size: 60,
  });
  const col_property_sale_id = columnHelper.accessor("property_sale_id", {
    id: "property_sale_id",
    header: "",
    cell: (e) =>
      <Button type="button" size="sm" variant="danger" onClick={() => e.table.options.meta.handleDelete({ row: e.row })}>
        Remove Property From Sale
      </Button>,
    size: 60,
  });
  const col_property_key_link = columnHelper.accessor("property_key_link", {
    id: "property_key_link",
    header: "",
    cell: (e) => {
      const propertyKey = e.row?.original?.property_key;

      if (!propertyKey) {
        return null;
      }

      return (
        <Link
          to={{
            pathname: `/properties/${propertyKey}`,
            state: {
              tab: "sales",
              data: Array.isArray(propertyidlist) && propertyidlist.indexOf(propertyKey) >= 0 ? propertyidlist : [],
            },
          }}
        >
          <Button type="button" size="sm" variant="primary">
            Go to {propertyKey}
          </Button>
        </Link>
      );
    },
    size: 120,
    enableSorting: false,
  });
  const col_property_key_entity_link = columnHelper.accessor("property_key", {
    id: "property_key_entity_link",
    header: "",
    cell: (e) => {
      const propertyKey = e.row?.original?.property_key;

      if (!propertyKey) {
        return null;
      }

      return <Link to={`/properties/${propertyKey}`}>{propertyKey}</Link>;
    },
    size: 100,
    enableSorting: false,
  });
  const col_sale_date_readonly = columnHelper.accessor("sale_date", {
    id: "sale_date_readonly",
    header: "Sale Date",
    cell: (e) => formatSaleDate(e.getValue()),
    size: 120,
  });
  const col_sale_price_readonly = columnHelper.accessor("sale_price", {
    id: "sale_price_readonly",
    header: "Sale Price",
    size: 120,
    meta: { align: "left" },
  });
  const col_property_key = columnHelper.accessor("property_key", {
    id: "property_key",
    header: "Cityscope Ref",
    cell: EditableCell,
    size: 100,
    meta: { uppercase: true },
  });
  const col_cityscope_ref = columnHelper.accessor("property_key", {
    id: "property_key",
    header: "Cityscope Ref",
    cell: EditableCell,
    size: 100,
    meta: { uppercase: true },
  });
  const col_property_classification = columnHelper.accessor("property_classification", {
    id: "property_classification",
    header: "Classification",
    cell: EditableCell,
    size: 100,
    style: { "white-space": "unset" },
  });
  const col_year_built = columnHelper.accessor("year_built", {
    id: "year_built",
    header: "Year Built",
    cell: EditableCell,
    size: 50,
  });
  const col_site_area = columnHelper.accessor("site_area", {
    id: "site_area",
    header: "Site Area",
    cell: EditableCell,
    maxWidth: 50,
    style: { textAlign: "right" },
    Footer: <b>{areaSum} </b>,
    size: 50,
  });
  const col_title_details = columnHelper.accessor("title_details", {
    id: "title_details",
    header: "Title",
    cell: EditableCell,
    size: 60,
    style: { "white-space": "unset" },
  });
  const col_withhold_flag = columnHelper.accessor("withhold_flag", {
    id: "withhold_flag",
    header: "Hold",
    cell: EditableCell,
    size: 50,
    style: { textAlign: "center" },
    enableSorting: false,
    meta: { type: "checkbox" },
  });
  const col_property_sale_id_property = columnHelper.accessor("col_property_sale_id_property", {
    id: "col_property_sale_id_property",
    header: "",
    cell: (e) =>
      <Button type="button" size="sm" variant="danger" onClick={() => e.table.options.meta.handleDelete({ row: e.row })}>
        Remove Property
      </Button>,
    size: 140,
  });

  const col_property_classification_readonly = columnHelper.accessor("property_classification", {
    id: "property_classification_readonly",
    header: "Classification",
    size: 100,
    style: { "white-space": "unset" },
  });

  const col_year_built_readonly = columnHelper.accessor("year_built", {
    id: "year_built_readonly",
    header: "Year Built",
    size: 50,
  });

  const col_site_area_readonly = columnHelper.accessor("site_area", {
    id: "site_area_readonly",
    header: "Site Area",
    maxWidth: 50,
    style: { textAlign: "right" },
    Footer: <b>{areaSum} </b>,
    size: 50,
  });

  const col_title_details_readonly = columnHelper.accessor("title_details", {
    id: "title_details_readonly",
    header: "Title",
    size: 60,
    style: { "white-space": "unset" },
  });

  const col_withhold_flag_readonly = columnHelper.accessor("withhold_flag", {
    id: "withhold_flag_readonly",
    header: "Hold",
    cell: (e) => (
      <div style={{ textAlign: "center" }}>
        <input type="checkbox" checked={Boolean(e.getValue())} disabled />
      </div>
    ),
    size: 50,
    style: { textAlign: "center" },
    enableSorting: false,
  });

  const col_remove_hold_row = columnHelper.accessor("remove_hold_row", {
    id: "remove_hold_row",
    header: "",
    cell: (e) => (
      <Button
        type="button"
        size="sm"
        variant="danger"
        style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
        disabled={!trustProof || !e.row.original?.withhold_flag || Boolean(e.row.original?.new_record)}
        onClick={() => e.table.options.meta.handleDelete({ row: e.row })}
      >
        Remove Property
      </Button>
    ),
    size: 130,
    enableSorting: false,
  });

    const columns = [
      col_property_key_link,
      col_cityscope_ref,
      col_sale_id,
      col_last_sale,
      col_site_area,
      col_appendix,
      col_income,
      col_income_basis,
      col_yield,
      col_withhold_flag,
      col_property_sale_id,
    ];

    const columns1 = [
      col_property_key_link,
      col_property_key,
      col_last_sale,
      col_property_classification,
      col_year_built,
      col_site_area,
      col_title_details,
      col_withhold_flag,
      col_property_sale_id_property,
    ];

    const columns2 = [
      col_property_key_link,
      col_cityscope_ref,
      col_last_sale,
      col_property_classification_readonly,
      col_year_built_readonly,
      col_site_area_readonly,
      col_title_details_readonly,
      col_withhold_flag_readonly,
      col_remove_hold_row,
    ];

    const columns4 = [
      col_property_key_entity_link,
      col_sale_date_readonly,
      col_sale_price_readonly,
      col_property_classification_readonly,
      col_year_built_readonly,
      col_title_details_readonly,
    ];

    const columnsMap = {
      table1: columns1,
      table2: columns2,
      "entity-table4": columns4,
      table: columns,
    };

    function updateRowData(rowIndex, columnId, value) {
      const localData = [...tableData];
      localData[rowIndex] = {
        ...localData[rowIndex],
        [columnId]: value,
      };
      updateTableState(localData, dataSet);
    }

    function normalizeCollectionResponse(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (!payload || typeof payload !== "object") {
        return [];
      }

      const keysToCheck = ["propertySales", "items", "data", "results", "rows", "records"];
      for (let i = 0; i < keysToCheck.length; i++) {
        const key = keysToCheck[i];
        if (Array.isArray(payload[key])) {
          return payload[key];
        }
      }

      return [];
    }

    async function fetchPropertySalesForSale(saleId) {
      const response = await get({
        apiName: "colin",
        path: "/propertysales",
        options: {
          queryParams: { saleid: saleId },
        },
      }).response;

      const body = await response.body.json();
      return normalizeCollectionResponse(body);
    }

    async function handleSave({ row, column, value }) {
      const normalizedValue = column.id === "property_key" && typeof value === "string"
        ? value.toUpperCase()
        : value;

      const record = {
        ...row.original,
        [column.id]: normalizedValue,
      };

      try {
        let result;
        
        // For new records, use configured CoLin API endpoint via Amplify
        if (record.new_record) {
          // Only Cityscope Ref should trigger creation/validation for new rows.
          if (column.id !== "property_key") {
            return true;
          }

          if (!normalizedValue || String(normalizedValue).trim() === "") {
            setErrorMessage("Enter a Cityscope reference.");
            setShowErrorModal(true);
            return false;
          }

          if (typeof validateRelatedRecord === "function") {
            const [isValid, validationMessage] = validateRelatedRecord(record, dataSet);
            if (!isValid) {
              setErrorMessage(validationMessage || "Validation failed.");
              setShowErrorModal(true);
              return false;
            }
          }

          const response = await post({
            apiName: "colin",
            path: "/propertysales/new",
            options: {
              body: record,
            },
          }).response;

          result = await response.body.json();
        } else {
          // For existing records, use saveRecord
          result = await saveRecord(record, dataSet, "property_sale_id");
        }

        if (result && result.result === false) {
          setErrorMessage(result.error_message || "Failed to save record");
          setShowErrorModal(true);
          return false;
        }

        if (record.new_record) {
          const resolvedSaleId = record.sale_id || saleid;

          // Source of truth: reload from API endpoint /propertysales?saleid=... so row fields are fully populated.
          if (resolvedSaleId) {
            try {
              const hydratedRows = await fetchPropertySalesForSale(resolvedSaleId);
              if (hydratedRows.length > 0) {
                updateTableState(hydratedRows, dataSet);
              }
            } catch (refreshErr) {
              console.warn("Fallback refresh from /propertysales failed", refreshErr);
            }
          }

          if (controlType === "table2" && typeof getProperties === "function" && resolvedSaleId) {
            await getProperties({ saleid: resolvedSaleId });
          }

          if (!resolvedSaleId) {
            const updatedRows = [...tableData];
            updatedRows[row.index] = {
              ...updatedRows[row.index],
              ...record,
              ...(result && typeof result === "object" ? result : {}),
              property_key: normalizedValue,
              new_record: false,
              modified: false,
              ...(result?.id ? { property_sale_id: result.id } : {}),
            };
            updateTableState(updatedRows, dataSet);
          }

          // Tell EditableCell not to overwrite the refreshed dataset with stale local input.
          return { skipLocalUpdate: true };
        }
        
        return !(result && result.result === false);
      } catch (err) {
        console.error("Save failed:", err);
        setErrorMessage(err.message || "An error occurred while saving");
        setShowErrorModal(true);
        return false;
      }
    }

    async function handleDelete({ row }) {
      const confirmed = window.confirm("Are you sure you want to remove this property from the sale?");
      if (!confirmed) {
        return false;
      }

      const record = {
        ...row.original,
        last_edited_by: user,
      };

      const isTempRecord = Boolean(record.new_record || !record.property_sale_id || String(record.property_sale_id).startsWith("temp-"));

      if (isTempRecord) {
        const updatedRows = tableData.filter((_, index) => index !== row.index);
        updateTableState(updatedRows, dataSet);
        return true;
      }

      try {
        const result = await saveRecord(
          {
            ...record,
            archived_date: new Date().toISOString(),
          },
          dataSet,
          "property_sale_id"
        );

        if (result && result.result === false) {
          setErrorMessage(result.error_message || "Failed to remove property from sale.");
          setShowErrorModal(true);
          return false;
        }

        const updatedRows = tableData.filter((_, index) => index !== row.index);
        updateTableState(updatedRows, dataSet);

        // Avoid immediate re-fetch here: some API responses can still include the
        // archived row and reinsert it into the UI right after local removal.

        return true;
      } catch (err) {
        setErrorMessage(err?.message || "Failed to remove property from sale.");
        setShowErrorModal(true);
        return false;
      }
    }

    async function handleRemoveHoldRow({ row }) {
      const record = {
        ...row.original,
        withhold_flag: false,
        last_edited_by: user,
      };

      try {
        const result = await saveRecord(record, dataSet, "property_sale_id");

        if (result && result.result === false) {
          setErrorMessage(result.error_message || "Failed to remove hold.");
          setShowErrorModal(true);
          return false;
        }

        const updatedRows = [...tableData];
        updatedRows[row.index] = {
          ...updatedRows[row.index],
          withhold_flag: false,
        };
        updateTableState(updatedRows, dataSet);

        const resolvedSaleId = row.original?.sale_id || saleid;
        if (typeof getProperties === "function" && resolvedSaleId) {
          await getProperties({ saleid: resolvedSaleId });
        }

        return true;
      } catch (err) {
        setErrorMessage(err?.message || "Failed to remove hold.");
        setShowErrorModal(true);
        return false;
      }
    }

    async function addRecord(event) {
      let saleData = [...data];
      saleData.unshift({
        sale_id: saleid,
        property_id: null,
        property_key: "",
        withhold_flag: false,
        last_sale: false,
        created_by: user,
        last_edited_by: user,
        new_record: true,
      });
      updateTableState(saleData, dataSet);
    }

  function renderComponent() {
    const activeColumns = columnsMap[resolvedControlType] || columnsMap.table;

    return (
      <div className="sales">
        {(resolvedControlType === "table" || resolvedControlType === "table2") && (
          <Row>
            <Col className="text-right">
              {/*   <Button variant="secondary" size="sm" onClick={refreshDataSet}  >Refresh</Button>  */}
              {resolvedControlType === "table" && (
                <Link
                  to={{
                    pathname: `/properties/${propertykey}/sales/new?propertyid=${propertyid}`,
                    state: {
                      primaryCategory: primaryCategory,
                      isUnit: IsUnit,
                    },
                  }}
                >
                  <Button variant="secondary" size="sm">
                    Add
                  </Button>
                </Link>
              )}
              <div style={{marginBottom: '8px'}}>
                {resolvedControlType === "table2" && (
                  <Button variant="danger" disabled={!trustProof} size="sm" onClick={removeHold} style={{ marginRight: "8px" }}>
                    Remove HOLD
                  </Button>
                )}
                {resolvedControlType === "table2" && (
                  <Button variant="secondary" disabled={!trustProof} size="sm" onClick={addRecord}>
                    Add
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        )}
        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : (
              <GenericTable
                columns={activeColumns}
                data={tableData}
                section={`property-sales-${controlType || "table"}`}
                pageSize={resolvedPageSize}
                enablePagination={true}
                enforceMinRowsHeight={true}
                minVisibleRows={5}
                elementIds={tableElementIds}
                meta={{
                  updateData: updateRowData,
                  handleSave,
                  handleDelete,
                  handleRemoveHoldRow,
                  shouldValidateUnchangedOnBlur: ({ row, column }) => Boolean(row?.original?.new_record && column?.id === "property_key"),
                }}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="SaleList">
      {isAuthenticated ? renderComponent() : null}
      
      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger mb-0" role="alert">
            {errorMessage}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowErrorModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EntityPropertySaleList;